import { createListCollection, Text, VStack } from '@chakra-ui/react';
import { CustomSelect } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectAltitudeUnit, selectHeightSuffix, selectHeightUnit } from '@redux/settings/settings.selectors';
import { usePatchAltitudeUnitMutation, usePatchHeightUnitMutation } from '@services/config/setting.api';
import { HeightUnits } from '@utils/unit.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export default function UnitsSettings() {
  const { login } = useAppSelector(selectActiveUser);
  const { formatMessage } = useIntl();
  const heightUnit = useAppSelector(selectHeightUnit);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const showHeightSuffix = useAppSelector(selectHeightSuffix);

  const [localHeightUnit, setLocalHeightUnit] = useState<HeightUnits>(heightUnit);
  const [localAltitudeUnit, setLocalAltitudeUnit] = useState<HeightUnits>(altitudeUnit);

  const [patchHeightUnit] = usePatchHeightUnitMutation();
  const [patchAltitudeUnit] = usePatchAltitudeUnitMutation();

  const handleHeightUnit = (newHeightUnit: HeightUnits) => {
    setLocalHeightUnit(newHeightUnit);
    patchHeightUnit({ login, heightUnit: newHeightUnit })
      .unwrap()
      .catch(() => setLocalHeightUnit(heightUnit));
  };

  const handleAltitudeUnit = (newAltitudeUnit: HeightUnits) => {
    setLocalAltitudeUnit(newAltitudeUnit);
    patchAltitudeUnit({ login, altitudeUnit: newAltitudeUnit })
      .unwrap()
      .catch(() => setLocalHeightUnit(altitudeUnit));
  };

  const heightItems = createListCollection({
    items: Object.values(HeightUnits).map((unit) => ({
      value: unit,
      label: formatMessage({ id: `components.userSettings.units.heightUnits.${unit}` }),
    })),
  });

  return (
    <VStack backgroundColor="neutral.800" flexShrink={0} gap={4} padding={4} alignItems="start">
      <Text fontSize="22px">
        <FormattedMessage id="components.userSettings.units.title" />
      </Text>
      <VStack gap={2} alignItems="start" height="100%">
        <VStack gap={1} alignItems="start">
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage id="components.userSettings.units.height" values={{ showHeightSuffix }} />
          </Text>
          <CustomSelect
            value={localHeightUnit}
            collection={heightItems}
            width="210px"
            size="sm"
            onChange={(value) => handleHeightUnit(value as HeightUnits)}
          />
        </VStack>
        <VStack gap={1} alignItems="start">
          <Text textStyle="md" color="neutral.300">
            <FormattedMessage id="components.userSettings.units.altitude" values={{ showHeightSuffix }} />
          </Text>
          <CustomSelect
            value={localAltitudeUnit}
            collection={heightItems}
            width="210px"
            size="sm"
            onChange={(value) => handleAltitudeUnit(value as HeightUnits)}
          />
        </VStack>
      </VStack>
    </VStack>
  );
}
